import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { HiMinus, HiPlus, HiRocketLaunch } from "react-icons/hi2";
import { FaFortAwesome, FaSocks, FaTicketAlt, FaGolfBall } from "react-icons/fa";
import { FiInfo, FiPlay, FiCreditCard, FiLock } from "react-icons/fi";
import { supabase } from "../../supabase";
import { useNavigate } from "react-router-dom";
import BottomSheet from "../../components/common/BottomSheet";
import { translateProductName } from "../../utils/translateProductName";
import "./Products.css";

// Mirrors the real .product-card row layout (thumb square + title/price + qty circle)
// so the skeleton doesn't jump/resize once real products load in.
function ProductRowSkeleton() {
  return (
    <div className="product-card">
      <div className="product-thumb skeleton-shimmer" />
      <div className="product-info">
        <div className="skeleton-shimmer" style={{ height: 15, width: '55%', borderRadius: 4, marginBottom: 8 }} />
        <div className="skeleton-shimmer" style={{ height: 13, width: '30%', borderRadius: 4 }} />
      </div>
      <div className="qty-control">
        <div className="skeleton-shimmer" style={{ width: 34, height: 34, borderRadius: '50%' }} />
      </div>
    </div>
  );
}

function ProductsSkeleton() {
  return (
    <div className="products-wrapper">
      <div className="filter-wrap">
        <div className="skeleton-shimmer" style={{ height: 40, width: '100%', borderRadius: 10 }} />
      </div>
      {[0, 1].map((section) => (
        <div key={section} className="category-section">
          <div className="skeleton-shimmer" style={{ height: 12, width: 90, borderRadius: 4, margin: '28px 0 10px' }} />
          {[0, 1, 2].map((row) => <ProductRowSkeleton key={row} />)}
        </div>
      ))}
    </div>
  );
}

// Read synchronously on mount so a repeat visit (data already cached from a prior
// fetch this session) can paint instantly instead of skeleton-flashing every time.
const PRODUCTS_CACHE_KEY = 'pws_products_cache';
const readCachedProducts = () => {
  try { return JSON.parse(localStorage.getItem(PRODUCTS_CACHE_KEY)) || []; } catch { return []; }
};
const readSavedQuantities = () => {
  try { return JSON.parse(localStorage.getItem("productQuantities")) || {}; } catch { return {}; }
};

export default function Products() {
  const { t, i18n } = useTranslation('products');
  const [products, setProducts] = useState(readCachedProducts);
  const [quantities, setQuantities] = useState(readSavedQuantities);
  const [loading, setLoading] = useState(() => readCachedProducts().length === 0);
  const [showInfoSheet, setShowInfoSheet] = useState(false); // "How this page works" explainer
  const [selectedCategory, setSelectedCategory] = useState('');
  const [appConfig, setAppConfig] = useState(null); // Firestore-configured bonuses
  const [shouldAnimate, setShouldAnimate] = useState(false);
  // Klippekort is account-bound (guest checkout can never buy it), so it's hidden
  // from anyone not logged in entirely - rather than letting them add it and only
  // finding out it's blocked once they reach the cart. Seeded from the same
  // "were they logged in last time" cache Home/Settings already use, so a repeat
  // visit doesn't flash klippekort in and then hide it once auth resolves.
  const [isLoggedIn, setIsLoggedIn] = useState(() => localStorage.getItem('pws_auth') === '1' ? true : null);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => setIsLoggedIn(!!session?.user));
    return () => subscription.unsubscribe();
  }, []);

  // Helper for haptic feedback
  const vibrate = () => {
    if (navigator.vibrate) navigator.vibrate(30);
  };

  // Check if this is a fresh page load or in-app navigation
  useEffect(() => {
    const hasVisitedProducts = sessionStorage.getItem('visitedProducts');
    if (!hasVisitedProducts) {
      // First visit in this session - enable animation
      setShouldAnimate(true);
      sessionStorage.setItem('visitedProducts', 'true');
    } else {
      // In-app navigation - disable animation
      setShouldAnimate(false);
    }
  }, []);

  // Save quantities to localStorage whenever quantities change
  useEffect(() => {
    localStorage.setItem("productQuantities", JSON.stringify(quantities));
  }, [quantities]);

  useEffect(() => {
    async function fetchProducts() {
      try {
        const { data: rows, error } = await supabase.from("products").select("*").order("category");
        if (error) throw error;
        const productList = (rows || [])
          .filter(product => product.active !== false) // Only show active products
          .map(p => ({
            id: p.id,
            name: p.name,
            category: p.category,
            price: p.price,
            duration: p.duration,
            bonusMinutes: p.bonus_minutes,
            appBonusMinutes: p.app_bonus_minutes,
            type: p.type,
            active: p.active,
          }));
        setProducts(productList);
        try { localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(productList)); } catch {}
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, []);

  // Fetch app config for bonuses (app_settings key='appConfig')
  useEffect(() => {
    async function fetchAppConfig() {
      try {
        const { data, error } = await supabase.from("app_settings").select("value").eq("key", "appConfig").maybeSingle();
        if (error) throw error;
        setAppConfig(data?.value || {});
      } catch (e) {
        console.error("Failed to fetch appConfig:", e);
        setAppConfig({});
      }
    }
    fetchAppConfig();
  }, []);

  if (loading) {
    return <ProductsSkeleton />;
  }

  const groupedProducts = products.reduce((groups, product) => {
    const category = product.category || "Other";
    if (!groups[category]) groups[category] = [];
    groups[category].push(product);
    return groups;
  }, {});

  Object.keys(groupedProducts).forEach(category => {
    groupedProducts[category].sort((a, b) => a.price - b.price);
  });

  const incrementQuantity = (productId, max) => {
    setQuantities(prev => {
      const current = prev[productId] || 0;
      if (max && current >= max) return prev;
      return { ...prev, [productId]: current + 1 };
    });
  };

  const decrementQuantity = (productId) => {
    setQuantities(prev => {
      const current = prev[productId] || 0;
      return { ...prev, [productId]: current > 0 ? current - 1 : 0 };
    });
  };

  const totalQuantity = Object.values(quantities).reduce((sum, q) => sum + q, 0);
  const totalPrice = products.reduce(
    (sum, p) => sum + (quantities[p.id] || 0) * p.price,
    0
  );

  const getBonusForProduct = (product) => {
    // 1) Product-level overrides
    try {
      if (product && product.bonusEnabled === false) return 0;
      if (product && typeof product.appBonusMinutes === 'number' && Number.isFinite(product.appBonusMinutes)) {
        return product.appBonusMinutes;
      }
      if (product && (typeof product.appBonusMinutes === 'string')) {
        const n = parseInt(product.appBonusMinutes, 10);
        if (Number.isFinite(n)) return n;
      }
      // Support alias: bonusTime (minutes)
      if (product && (typeof product.bonusTime === 'number' || typeof product.bonusTime === 'string')) {
        const n = typeof product.bonusTime === 'number' ? product.bonusTime : parseInt(product.bonusTime, 10);
        if (Number.isFinite(n)) return n;
      }
      if (product && (typeof product.bonusMinutes === 'number' || typeof product.bonusMinutes === 'string')) {
        const n = typeof product.bonusMinutes === 'number' ? product.bonusMinutes : parseInt(product.bonusMinutes, 10);
        if (Number.isFinite(n)) return n;
      }
    } catch (_) {}

    // 2) Global appConfig fallback (per-category)
    if (!appConfig || appConfig.bonusEnabled === false) return 0;
    const categoryKey = String(product?.category || '').toLowerCase();
    const channels = Array.isArray(appConfig.channels) ? appConfig.channels.map(c => String(c).toLowerCase()) : [];
    const isAppChannelEnabled = channels.length === 0 || channels.includes('app');
    if (!isAppChannelEnabled) return 0;
    const bonusByCategory = appConfig.bonusByCategory || {};
    const bonus = bonusByCategory[categoryKey];
    const bonusNum = typeof bonus === 'number' ? bonus : parseInt(bonus, 10);
    return Number.isFinite(bonusNum) ? bonusNum : 0;
  };

  const handleAddAllToCart = () => {
    const selectedProducts = products.filter(p => quantities[p.id] > 0);
    if (selectedProducts.length === 0) {
      toast.error(t('selectQuantityBeforePayment'));
      return;
    }

    const cartItems = selectedProducts.flatMap(p => {
      const qty = quantities[p.id] || 0;
      const bonusMinutes = getBonusForProduct(p);
      // Use a 6-digit random number for short unique ID
      let shortRand = Math.floor(Math.random() * 900000 + 100000); // 6 digits
      // Helper to get a 2-letter code from idx (AA, AB, AC, ..., ZZ)
      const getLetterCode = idx => {
        const first = String.fromCharCode(65 + Math.floor(idx / 26));
        const second = String.fromCharCode(65 + (idx % 26));
        return first + second;
      };
      if (p.category === 'klippekort' && p.type === 'stampCardTicket' && qty > 1) {
        return Array.from({ length: qty }).map((_, idx) => ({
          ...p,
          quantity: 1,
          bonusMinutes,
          productId: p.id,
          id: `KLK${shortRand + idx > 999999 ? (shortRand % 1000000) : shortRand + idx}${(idx > 899999 ? getLetterCode(idx - 900000) : '')}`,
        }));
      } else if (p.category === 'klippekort' && p.type === 'stampCardTicket') {
        return [{
          ...p,
          quantity: qty,
          bonusMinutes,
          productId: p.id,
          id: `KLK${shortRand}`,
        }];
      } else {
        return [{
          ...p,
          quantity: qty,
          bonusMinutes,
          productId: p.id,
          id: `${String(p.id || p.productId || '')}${Date.now()}`,
        }];
      }
    });

    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    localStorage.setItem("cartTimestamp", String(Date.now()));
    navigate("/mycart");
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case "golf":       return <FaGolfBall size={28} />;
      case "lek":        return <FaFortAwesome size={28} />;
      case "klippekort": return <FaTicketAlt size={28} />;
      case "tilbehør":   return <FaSocks size={28} />;
      default:           return <HiRocketLaunch size={28} />;
    }
  };

  const getCategoryColorClass = (category) => {
    switch (category?.toLowerCase()) {
      case "golf":       return "green";
      case "lek":        return "orange";
      case "klippekort": return "purple";
      case "tilbehør":   return "pink";
      default:           return "blue";
    }
  };

  const categoryOrder = ["golf", "lek", "klippekort", "tilbehør"];
  const orderedCategories = categoryOrder
    .map(cat => Object.keys(groupedProducts).find(key => key.toLowerCase() === cat))
    .filter(Boolean)
    .concat(Object.keys(groupedProducts).filter(key => !categoryOrder.includes(key.toLowerCase())));

  // Get all available categories for dropdown
  const allCategories = Object.keys(groupedProducts);

  return (
    <div className="products-wrapper">

      <div className="products-header-row">
        <div className="filter-wrap">
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
          >
            <option value="">{t('allCategories')}</option>
            {allCategories.map(cat => (
              <option key={cat} value={cat}>{t(`categories.${cat}`, { defaultValue: cat.charAt(0).toUpperCase() + cat.slice(1) })}</option>
            ))}
          </select>
        </div>
        <button className="products-info-btn" onClick={() => setShowInfoSheet(true)} aria-label={t('howPurchaseWorks')} title={t('howPurchaseWorks')}>
          <FiInfo size={18} />
        </button>
      </div>

      {orderedCategories
        .filter(category => !selectedCategory || category === selectedCategory)
        .map(category => {
          // Klippekort/månedskort are account-bound - guest checkout can never buy
          // them, so the whole section is shown locked (not hidden - still worth
          // discovering) rather than letting a guest add one only to be blocked
          // once they reach the cart.
          const isLockedCategory = isLoggedIn === false &&
            (category === 'klippekort' || category === 'månedskort');
          return (
          <div key={category} className="category-section">
            <div className="section-label">
              {t(`categories.${category}`, { defaultValue: category.charAt(0).toUpperCase() + category.slice(1) })}
            </div>

            <div className={isLockedCategory ? 'category-cards-group category-cards-group--locked' : 'category-cards-group'}>
            {isLockedCategory && (
              <div className="category-lock-overlay">
                <button
                  type="button"
                  className="category-lock-badge"
                  onClick={() => navigate('/login')}
                >
                  <FiLock size={18} />
                  <span>{t('loginForKlippekort')}</span>
                </button>
              </div>
            )}
            {groupedProducts[category].map(product => {
              const qty = quantities[product.id] || 0;
              // Klippekort is capped at 1 per purchase - the overall "max 3 active
              // klippekort" cap is separately enforced at checkout in MyCart.jsx.
              const maxQty = product.category === 'klippekort' ? 1 : undefined;
              return (
                <div
                  key={product.id}
                  className={`product-card${shouldAnimate ? ' animate-in' : ''}${qty > 0 ? ' has-qty' : ''}`}
                >
                  <div className={`product-thumb ${getCategoryColorClass(product.category)}`}>
                    {getCategoryIcon(product.category)}
                  </div>
                  <div className="product-info">
                    <h3>{translateProductName(product.name, i18n.language)}</h3>
                    <div className={`price${qty > 0 ? ' active' : ''}`}>
                      {qty > 0
                        ? `${product.price} kr · ${product.price * qty} kr`
                        : `${product.price} kr`}
                    </div>
                    {(product.category === 'lek' || product.category === 'klippekort') && getBonusForProduct(product) > 0 && (
                      <span className="bonus-badge">{t('bonusBadge', { n: getBonusForProduct(product) })}</span>
                    )}
                  </div>
                  <div className="qty-control">
                    <button
                      className="qty-btn minus"
                      onClick={() => { vibrate(); decrementQuantity(product.id); }}
                      aria-label={t('decreaseQtyFor', { name: translateProductName(product.name, i18n.language) })}
                    >
                      <HiMinus size={16} />
                    </button>
                    <span className={`qty-num${qty > 0 ? ' active' : ''}`}>{qty}</span>
                    <button
                      className="qty-btn plus"
                      onClick={() => { vibrate(); incrementQuantity(product.id, maxQty); }}
                      disabled={maxQty !== undefined && qty >= maxQty}
                      aria-label={t('increaseQtyFor', { name: translateProductName(product.name, i18n.language) })}
                    >
                      <HiPlus size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
          );
        })}

      <div className="reminder">{t('didYouForgetAnything')}</div>

      {totalQuantity > 0 && (
        <div className="summary-card">
          <div className="summary-title">{t('summary')}</div>
          <div className="summary-rows">
            {products
              .filter(p => (quantities[p.id] || 0) > 0)
              .map(p => (
                <div key={p.id} className="summary-row">
                  <div className="summary-row-left">
                    <div className="qty-badge">{quantities[p.id]}</div>
                    <div className="summary-row-text">
                      <span className="summary-row-name">{translateProductName(p.name, i18n.language)}</span>
                      {quantities[p.id] > 1 && (
                        <span className="summary-row-unit-price">{p.price} kr/stk</span>
                      )}
                    </div>
                  </div>
                  <strong className="summary-row-price">{quantities[p.id] * p.price} kr</strong>
                </div>
              ))}
          </div>
          <div className="summary-divider" />
          <div className="summary-total-row">
            <span>{t('total')}</span>
            <span className="total-amount">{totalPrice} kr</span>
          </div>
          <button
            className="btn-checkout"
            onClick={() => { vibrate(); handleAddAllToCart(); }}
          >
            {t('goToPayment')}
          </button>
          <button
            className="btn-clear"
            onClick={() => { vibrate(); setQuantities({}); }}
          >
            {t('clearAll')}
          </button>
        </div>
      )}
      {showInfoSheet && (
        <BottomSheet
          icon={<FiInfo size={20} />}
          iconColor="blue"
          title={t('howPurchaseWorks')}
          onClose={() => setShowInfoSheet(false)}
        >
          <div className="products-info-block">
            <div className="products-info-block-icon products-info-block-icon--orange"><FiPlay size={16} /></div>
            <div>
              <div className="products-info-block-title">{t('infoActivationTitle')}</div>
              <div className="products-info-block-text">
                {t('infoActivationText')}
              </div>
            </div>
          </div>
          <div className="products-info-block">
            <div className="products-info-block-icon products-info-block-icon--purple"><FiCreditCard size={16} /></div>
            <div>
              <div className="products-info-block-title">{t('infoPaymentTitle')}</div>
              <div className="products-info-block-text">
                {t('infoPaymentText')}
              </div>
            </div>
          </div>
        </BottomSheet>
      )}
    </div>
  );
}